import appContext from 'legacy/appcontext';
import Vue from 'vue';
import AppEventBus from 'utils/appEventBus';
import {accountsService, dashboardService, projectsService, userService} from 'services';
import _ from 'lodash';
import {AccountsService} from 'services/AccountsService';
import HttpBatchService from 'api/BatchRequests/HttpBatchService';
import Promise from 'promise-polyfill';
import ApiPermissionDeniedError from 'utils/api/errors/ApiPermissionDeniedError';

_.noConflict();

const sidebarAbortController = new AbortController();
const batchAccountsService = new AccountsService(HttpBatchService);

function getInitialState () {
  const sidebarStorage = appContext.getStorageValue('sidebar') || {};

  return {
    isClosed: Boolean(sidebarStorage.isClosed),
    isInitiallyExpanded: false,
    openedByImportantAccountItem: true,
    fetchedAccountsSet: new Set([]),
    expandedAccountIds: [],
    expandedImportantAccountIds: {},
    loadingAccountIds: [],
    isAllAccountsExpanded: false,
    navigationInImportant: false,

    isMyDashboardsExpanded: false,
    isAllDashboardsExpanded: false,

    sidebarImportantItems: [],

    accountsById: {},
    projectsById: {},

    createAccountPermissions: [],
    createProjectPermissions: [],

    userDashboardIds: [],
    dashboardsList: [],

    // onboarding staff
    canExpandAccount: true
  };
}


function expandAllAccountsTree (context, path) {
  const promises = path.map(a => {
    context.commit('TOGGLE_EXPAND_ACCOUNT', {accountId: a.id, isOpen: true});
    return context.dispatch('FETCH_SIDEBAR_ACCOUNT_CHILD_NODES', a.id);
  });


  return Promise.all(promises).then(() => {
    if (context.rootGetters['accounts/currentAccountId']) {
      context.commit('EXPAND_ALL_ACCOUNTS', true);
    }
  });
}

function handleImportantNavigation (context, path) {
  const reversedPath = path.reverse();
  const importantAccounts = context.getters.sidebarImportantItems.filter(a => a.entity_type === 'ACCOUNT');
  const importantAccountsIdsSet = new Set(importantAccounts.map(a => a.id));

  const pathIds = [];

  // finding closest account to current path (important account closer to last account in path)
  for (const accIndex in reversedPath) {
    if (!Object.prototype.hasOwnProperty.call(reversedPath, accIndex)) {
      continue;
    }
    const accItem = reversedPath[accIndex];
    pathIds.push(accItem.id);
    if (importantAccountsIdsSet.has(accItem.id)) {
      const rootAccount = accItem.id;

      const promises = pathIds.map(i => {
        context.commit('TOGGLE_EXPAND_IMPORTANT_ACCOUNT', {
          rootId: rootAccount,
          accountId: i,
          isOpen: true
        });
        return context.dispatch('FETCH_SIDEBAR_ACCOUNT_CHILD_NODES', i);
      });
      return [true, Promise.all(promises)];
    }
  }

  return [false, null];
}

function sidebarUpdateMutation (context, path) {
  const initiallyExpanded = context.getters.isInitiallyExpanded;
  if (path.length === 0) {
    return;
  }
  context.commit('SET_INITIALLY_EXPANDED', true);
  if (context.getters.navigationInImportant || !initiallyExpanded) {
    const [success, promise] = handleImportantNavigation(context, path);
    if (success) {
      context.dispatch('SET_NAVIGATION_IN_IMPORTANT', true);
      return promise;
    }
  }
  context.dispatch('SET_NAVIGATION_IN_IMPORTANT', false);

  if (!context.getters.navigationInImportant) {
    return expandAllAccountsTree(context, path);
  }
}

const sidebarStore = {
  namespaced: true,
  state: getInitialState(),
  getters: {
    isSidebarClosed (state) {
      return state.isClosed;
    },
    isMyDashboardsExpanded: state => state.isMyDashboardsExpanded,
    isAllAccountsExpanded: (state) => state.isAllAccountsExpanded,
    isAllDashboardsExpanded: state => state.isAllDashboardsExpanded,

    isAccountExpanded: state => accountId => Boolean(state.expandedAccountIds.find(i => i === accountId)),

    isImportantAccountExpanded: state => (rootId, accountId) =>
      Boolean((state.expandedImportantAccountIds[rootId] || []).find(i => i === accountId)),

    sidebarImportantItems: state => state.sidebarImportantItems,

    isAccountImportant (state) {
      return id => Boolean(state.sidebarImportantItems.find(
        i => i.id === id && i.entity_type === 'ACCOUNT'
      ));
    },
    isDashboardImportant (state) {
      return id => Boolean(state.sidebarImportantItems.find(
        i => i.id === id && i.entity_type === 'DASHBOARD'
      ));
    },

    // accounts
    accountList (state) {
      return _.sortBy(Object.values(state.accountsById), 'name');
    },
    accountsByParent (state, getters) {
      return _.groupBy(getters.accountList, account => account.parent_id || null);
    },
    getChildAccountsById (state, getters) {
      return id => (getters.accountsByParent[id] || []).filter(a => a.status === 'ACTIVE');
    },
    getAccountById (state) {
      return id => state.accountsById[id] || null;
    },

    /*
     * getSidebarAccountChildren (state, getters) {
     *   return accountId => getters.getChildAccountsById(accountId);
     * },
     */
    sidebarRootAccounts: (state, getters) => getters.getChildAccountsById(null),
    isAccountLoading: state => accountId => Boolean(state.loadingAccountIds.find(i => i === accountId)),

    getAccountsListForAccount: (state, getters) => id => {
      const getAccountWithChildrenFlat = (parent) => {
        if (!parent) {
          return [];
        }

        return [
          parent,
          ...getters.getChildAccountsById(parent.id).map(
            i => getAccountWithChildrenFlat(i)
          ).
            flat()
        ];
      };
      const rootAccount = getters.getAccountById(id);

      return getAccountWithChildrenFlat(rootAccount);
    },
    getAccountParents (state, getters) {
      return id => {
        const getParent = (account) => {
          if (account.parent_id) {
            return [account, ...getParent(getters.getAccountById(account.parent_id))];
          }
          return [account];
        };
        const account = getters.getAccountById(id);

        // return empty list if account not found (it may be not fetched)
        if (!account || !account.parent_id) {
          return [];
        }

        // may be cases when parent account is not fetched
        const parent = getters.getAccountById(account.parent_id);
        if (!parent) {
          console.warn(`[store] account with id-${account.parent_id} is not fetched`);
          return [];
        }
        return getParent(parent);
      };
    },

    // projects
    projectsByAccountId (state) {

      // group by account id
      const projectsGroupedByAccountId = _.groupBy(
        Object.values(state.projectsById), project => project.account_id || null
      );

      // sort by name
      Object.keys(projectsGroupedByAccountId).forEach(accountId => {
        projectsGroupedByAccountId[accountId] = _.sortBy(
          projectsGroupedByAccountId[accountId].filter(p => p.status !== 'ARCHIVED'),
          'name');
      });

      return projectsGroupedByAccountId;
    },

    getAccountProjects (state, getters) {
      return accountId => getters.projectsByAccountId[accountId] || [];
    },
    getProjectById (state) {
      return projectId => state.projectsById[projectId];
    },

    // permissions
    hasCreateAccountPermissions (state) {
      return accountId => state.createAccountPermissions.includes(accountId);
    },
    hasCreateProjectPermissions (state) {
      return accountId => state.createProjectPermissions.includes(accountId);
    },

    // dashboards
    dashboardsList: state => state.dashboardsList,
    userDashboardsList (state) {
      const userDashboardIdsSet = new Set(state.userDashboardIds);
      return state.dashboardsList.filter(d => userDashboardIdsSet.has(d.id));
    },
    sharedDashboardsList (state) {
      const userDashboardIdsSet = new Set(state.userDashboardIds);
      return state.dashboardsList.filter(d => !userDashboardIdsSet.has(d.id));
    },
    getDashboardById (state) {
      return id => state.dashboardsList.find(i => i.id === id);
    },

    // onboarding staff
    canExpandAccount: (state) => state.canExpandAccount
  },
  mutations: {
    SET_IMPORTANT_ITEMS (state, items) {
      state.sidebarImportantItems = items;
    },

    // Accounts
    MERGE_ACCOUNTS (state, {accounts, replace = true}) {
      for (const account of accounts) {
        if (replace || !state.accountsById[account.id]) {
          Vue.set(state.accountsById, account.id, account);
        }
      }
    },
    SET_ACCOUNT_LOADING (state, {accountId, isLoading}) {
      if (isLoading) {
        state.loadingAccountIds.push(accountId);
      } else {
        state.loadingAccountIds = state.loadingAccountIds.filter(aId => aId !== accountId);
      }
    },
    SET_ACCOUNT_FETCHED (state, accountId) {
      state.fetchedAccountsSet.add(accountId);
    },
    DELETE_PROJECT (state, projectId) {
      Vue.delete(state.projectsById, projectId);
    },
    DELETE_ACCOUNT (state, accountId) {
      Vue.delete(state.accountsById, accountId);
    },

    // projects
    MERGE_PROJECTS (state, {projects}) {
      for (const project of projects) {
        const existingProject = state.projectsById[project.id];
        if (!existingProject || !existingProject._is_detailed) {
          Vue.set(state.projectsById, project.id, project);
        }
      }
    },

    // permissions
    ADD_CREATE_ACCOUNT_PERMISSION (state, accountId) {
      state.createAccountPermissions.push(accountId);
    },
    ADD_CREATE_PROJECT_PERMISSION (state, accountId) {
      state.createProjectPermissions.push(accountId);
    },
    REMOVE_CREATE_ACCOUNT_PERMISSION (state, accountId) {
      state.createAccountPermissions = state.createAccountPermissions.filter(aId => aId !== accountId);
    },
    REMOVE_CREATE_PROJECT_PERMISSION (state, accountId) {
      state.createProjectPermissions = state.createProjectPermissions.filter(aId => aId !== accountId);
    },

    // dashboards
    SET_USER_DASHBOARD_IDS (state, dashboardIdsList) {
      state.userDashboardIds = dashboardIdsList;
    },
    SET_DASHBOARDS_LIST (state, dashboardsList) {
      state.dashboardsList = dashboardsList;
    },

    // navigation mutations
    SET_NAVIGATION_IN_IMPORTANT (state, val) {
      state.navigationInImportant = val;
    },

    // expand items
    TOGGLE_EXPAND_MY_DASHBOARD (state, expanded) {
      state.isMyDashboardsExpanded = expanded;
    },
    TOGGLE_EXPAND_All_DASHBOARD (state, expanded) {
      state.isAllDashboardsExpanded = expanded;
    },
    TOGGLE_EXPAND_ALL_ACCOUNTS (state) {
      state.isAllAccountsExpanded = !state.isAllAccountsExpanded;
    },
    EXPAND_ALL_ACCOUNTS (state, expanded = true) {
      state.isAllAccountsExpanded = expanded;
    },

    TOGGLE_EXPAND_IMPORTANT_ACCOUNT (state, {rootId, accountId, isOpen}) {
      if (isOpen) {
        if (!state.expandedImportantAccountIds[rootId]) {
          Vue.set(state.expandedImportantAccountIds, rootId, [accountId]);
          return;
        }
        state.expandedImportantAccountIds[rootId].push(accountId);
      } else {
        if (!state.expandedImportantAccountIds[rootId]) {
          Vue.set(state.expandedImportantAccountIds, rootId, []);
          return;
        }
        Vue.set(
          state.expandedImportantAccountIds,
          rootId,
          state.expandedImportantAccountIds[rootId].filter(
            aId => aId !== accountId
          )
        );
      }
    },

    TOGGLE_EXPAND_ACCOUNT (state, {accountId, isOpen}) {
      if (isOpen) {
        state.expandedAccountIds.push(accountId);
      } else {
        state.expandedAccountIds = state.expandedAccountIds.filter(aId => aId !== accountId);
      }
    },

    SET_INITIALLY_EXPANDED (state, val) {
      state.isInitiallyExpanded = val;
    },
    SET_SIDEBAR_CLOSED (state, {isClosed}) {
      state.isClosed = isClosed;

      // store in local storage
      const sidebarStorage = appContext.getStorageValue('sidebar') || {};
      sidebarStorage.isClosed = isClosed;
      appContext.setStorageValue('sidebar', sidebarStorage);
    },

    // important accounts
    TOGGLE_ITEM_IMPORTANT (state, {item, important}) {
      const existingItem = state.sidebarImportantItems.find(
        i => i.id === item.id && i.entity_type === item.entity_type
      );
      if (existingItem && !important) {

        // remove from important item
        state.sidebarImportantItems = state.sidebarImportantItems.filter(
          i => !(i.id === item.id && i.entity_type === item.entity_type)
        );
      } else if (!existingItem && important) {

        // add to important item
        state.sidebarImportantItems.push(item);
      }
    },
    SWAP_IMPORTANT_ITEMS (state, {newIndex, oldIndex}) {
      state.sidebarImportantItems.splice(
        newIndex,
        0,
        state.sidebarImportantItems.splice(oldIndex, 1)[0]
      );
    },

    // onboarding special staff
    TOGGLE_ENABLED_ACCOUNTS_EXPANDING (state, active) {
      state.canExpandAccount = active;
    },
    COLLAPSE_ALL_ITEMS (state) {
      state.isAllAccountsExpanded = false;
      state.expandedImportantAccountIds = {};
      state.expandedAccountIds = [];

      state.isMyDashboardsExpanded = false;
      state.isAllDashboardsExpanded = false;
    }
  },
  actions: {
    async FETCH_IMPORTANT_ITEMS (context) {
      const items = (await userService.getImportantItems({
        signal: sidebarAbortController.signal
      })).items;

      const accounts = items.filter(i => i.entity_type === 'ACCOUNT').map(a => {
        const account = _.omit(a, ['entity_type']);
        account.type = account.account_type;
        account.account_type = undefined;
        return account;
      });
      context.commit('MERGE_ACCOUNTS', {accounts, replace: false});
      context.commit('SET_IMPORTANT_ITEMS', items);
    },

    // Accounts
    async FETCH_ACCOUNT (context, accountId) {
      const account = await accountsService.get(accountId, {signal: sidebarAbortController.signal});

      context.commit('MERGE_ACCOUNTS', {accounts: [account]});
    },
    async FETCH_ACCOUNTS_BATCH (context, accountIds) {
      const accountsPromise = accountIds.map(async accountId => {
        try {
          return await batchAccountsService.get(accountId);
        } catch (e) {
          if (e instanceof ApiPermissionDeniedError) {
            console.warn(`[Vuex sidebar] permission denied for account {${accountId}}. Skipping...`);
            return null;
          }
          throw e;
        }
      });
      await HttpBatchService.makeBatchRequest();
      const accounts = (await Promise.all(accountsPromise)).filter(a => Boolean(a));

      context.commit('MERGE_ACCOUNTS', {accounts});
    },
    async FETCH_ROOT_ACCOUNTS (context) {
      const accounts = (
        await accountsService.getChildAccounts(null, {}, {signal: sidebarAbortController.signal})
      ).items;
      context.commit('MERGE_ACCOUNTS', {accounts});
    },
    async FETCH_ACCOUNTS_CHILD_ACCOUNTS_BATCH (context, accountIds) {
      const promises = accountIds.map(accountId => batchAccountsService.getChildAccounts(accountId));

      await HttpBatchService.makeBatchRequest();
      const childAccounts = (await Promise.all(promises)).map(batchGroup => batchGroup.items).flat();

      context.commit('MERGE_ACCOUNTS', {accounts: childAccounts});
    },
    async FETCH_ACCOUNT_CHILD_ACCOUNTS (context, accountId) {
      const childAccounts = (
        await accountsService.getChildAccounts(accountId, {}, {signal: sidebarAbortController.signal})
      ).items;

      if (childAccounts.length === 0) {
        const permissions = (
          await userService.getUserPermissions(accountId)
        ).items;

        const isCanCreateAccount = permissions.some(item => item.id === 'CREATE_ACCOUNT');

        if (isCanCreateAccount) {
          context.commit('ADD_CREATE_ACCOUNT_PERMISSION', accountId);
        }
      }

      context.commit('MERGE_ACCOUNTS', {accounts: childAccounts});
      return childAccounts;
    },
    FETCH_ACCOUNT_TREE_FOR_ACCOUNT (context, accountId) {
      const fetchChildrenForList = async (accountIdsList) => {
        if (accountIdsList.length === 0) {
          return [];
        }
        const p = accountIdsList.map(i => context.dispatch('FETCH_ACCOUNT_CHILD_ACCOUNTS', i));

        const accountIds = (await Promise.all(p)).reduce(
          (acc, pResult) => [...acc, ...pResult.map(i => i.id)], []
        );

        /*
         * filter out usual accounts because they don't have child accounts
         * and there is no need to create additional requests to get empty list
         */
        const filteredMccAccountIds = accountIds.filter(aId => {
          const account = context.getters.getAccountById(aId);
          if (!account) {
            return true;
          }
          return account.type === 'MCC';
        });

        return await fetchChildrenForList(filteredMccAccountIds);
      };

      return fetchChildrenForList([accountId]);
    },
    REMOVE_ACCOUNT_PROJECT (context, projectId) {
      const project = context.getters.getProjectById(projectId);
      const accountId = project.account_id;

      context.commit('DELETE_PROJECT', projectId);

      const canCreateProjectsInAccount = context.state.createProjectPermissions.includes(accountId);

      if (canCreateProjectsInAccount) {
        context.commit('REMOVE_CREATE_PROJECT_PERMISSION', accountId);
      }

      context.state.fetchedAccountsSet.delete(accountId);
      context.dispatch('FETCH_SIDEBAR_ACCOUNT_CHILD_NODES', accountId);
    },
    async DELETE_ACCOUNT (context, accountId) {
      const isImportant = context.getters.isAccountImportant(accountId);
      if (isImportant) {
        context.dispatch('TOGGLE_ACCOUNT_IMPORTANT', {accountId, important: false});
      }

      const parent = context.getters.getAccountParents(accountId);

      context.commit('DELETE_ACCOUNT', accountId);

      if (parent.length) {
        const parentId = parent[0].id;
        const canCreateAccountInParent = context.state.createAccountPermissions.includes(parentId);

        if (canCreateAccountInParent) {
          context.commit('REMOVE_CREATE_ACCOUNT_PERMISSION', parentId);
        }

        context.state.fetchedAccountsSet.delete(parentId);
        context.dispatch('FETCH_SIDEBAR_ACCOUNT_CHILD_NODES', parentId);
      }
    },

    // projects
    async FETCH_PROJECT (context, [accountId, projectId]) {
      const project = await projectsService.getProjectDetails(
        accountId,
        projectId,
        {signal: sidebarAbortController.signal}
      );

      context.commit('MERGE_PROJECTS', {projects: [project]});
      return project;
    },
    async FETCH_ACCOUNT_PROJECTS (context, accountId) {
      const projects = (await accountsService.getAccountProjects(
        accountId,
        {
          status: ['ACTIVE', 'PAUSED'],
          sorting: 'name'
        },
        {signal: sidebarAbortController.signal}
      )).items;

      if (projects.length === 0) {
        const permissions = (
          await userService.getUserPermissions(accountId)
        ).items;

        const isCanCreateProject = permissions.some(item => item.id === 'CREATE_PROJECT');

        if (isCanCreateProject) {
          context.commit('ADD_CREATE_PROJECT_PERMISSION', accountId);
        }
      }

      context.commit('MERGE_PROJECTS', {projects});
      return projects;
    },

    async FETCH_ACCOUNTS_PROJECTS_BATCH (context, accountIds) {
      const projects = (await projectsService.getAccountProjectsListSimple(accountIds, {
        status: ['ACTIVE', 'PAUSED'],
        sorting: 'name'
      })).items;

      context.commit('MERGE_PROJECTS', {projects});
      return _.groupBy(projects, 'account_id');
    },

    async FETCH_SIDEBAR_ACCOUNT_CHILD_NODES (context, accountId) {
      const accountsFetched = context.state.fetchedAccountsSet.has(accountId);
      if (!accountsFetched) {

        let account = context.getters.getAccountById(accountId);
        if (!account) {
          await context.dispatch('FETCH_ACCOUNT', accountId);
          account = context.getters.getAccountById(accountId);
        }
        context.commit('SET_ACCOUNT_LOADING', {accountId, isLoading: true});
        if (account.type === 'MCC') {
          await context.dispatch('FETCH_ACCOUNT_CHILD_ACCOUNTS', accountId);
        } else {
          await context.dispatch('FETCH_ACCOUNT_PROJECTS', accountId);
        }
        context.commit('SET_ACCOUNT_LOADING', {accountId, isLoading: false});
        context.commit('SET_ACCOUNT_FETCHED', accountId);
      }
    },

    // dashboards
    async FETCH_AVAILABLE_DASHBOARDS (context) {
      const [userDashboards, availableDashboards] = await Promise.all([
        await dashboardService.getUserDashboardsList({signal: sidebarAbortController.signal}),
        await dashboardService.getDashboardsList({signal: sidebarAbortController.signal})
      ]);

      const dashboardsList = [...userDashboards, ...availableDashboards];

      context.commit('SET_DASHBOARDS_LIST', _.uniqBy(dashboardsList, d => d.id));
      context.commit('SET_USER_DASHBOARD_IDS', userDashboards.map(d => d.id));
    },


    async SIDEBAR_UPDATE_STATE (context) {
      let expandAccountsPath = context.rootGetters['accounts/accountPath'];
      if (context.rootGetters['accounts/currentAccountId']) {
        expandAccountsPath = [...expandAccountsPath, {id: context.rootGetters['accounts/currentAccountId']}];
      }

      await sidebarUpdateMutation(context, expandAccountsPath);
      if (expandAccountsPath.length > 0) {
        AppEventBus.$emit('SIDEBAR_PATH_EXPAND_DATA_FETCHED');
      }
    },
    async SET_SIDEBAR_CLOSED (context, payload) {
      return context.commit('SET_SIDEBAR_CLOSED', payload);
    },

    // navigation actions
    SET_NAVIGATION_IN_IMPORTANT (context, val) {
      context.commit('SET_NAVIGATION_IN_IMPORTANT', val);
    },

    // expand
    TOGGLE_EXPAND_MY_DASHBOARD (context, toggleState) {
      context.commit('TOGGLE_EXPAND_MY_DASHBOARD', toggleState);
    },
    TOGGLE_EXPAND_ALL_DASHBOARD (context, toggleState) {
      context.commit('TOGGLE_EXPAND_All_DASHBOARD', toggleState);
    },
    TOGGLE_EXPAND_ALL_ACCOUNTS (context) {
      context.commit('TOGGLE_EXPAND_ALL_ACCOUNTS');
    },

    TOGGLE_EXPAND_ACCOUNT (context, accountId) {
      const isOpen = context.getters.isAccountExpanded(accountId);
      context.commit('TOGGLE_EXPAND_ACCOUNT', {accountId, isOpen: !isOpen});
      if (!isOpen) {
        context.dispatch('FETCH_SIDEBAR_ACCOUNT_CHILD_NODES', accountId);
      }
    },
    TOGGLE_EXPAND_IMPORTANT_ACCOUNT (context, {rootAccountId, accountId}) {
      const isOpen = context.getters.isImportantAccountExpanded(rootAccountId, accountId);
      context.commit(
        'TOGGLE_EXPAND_IMPORTANT_ACCOUNT',
        {
          rootId: rootAccountId,
          accountId,
          isOpen: !isOpen
        }
      );
      if (!isOpen) {
        context.dispatch('FETCH_SIDEBAR_ACCOUNT_CHILD_NODES', accountId);
      }
    },

    // important accounts
    async TOGGLE_ACCOUNT_IMPORTANT (context, payload) {
      const account = context.getters.getAccountById(payload.accountId);
      const accountData = {
        id: account.id,
        entity_type: 'ACCOUNT'
      };

      context.commit('TOGGLE_ITEM_IMPORTANT', {item: accountData, important: payload.important});
      return await context.dispatch('SAVE_IMPORTANT_ITEMS');
    },
    SWAP_IMPORTANT_ITEMS (context, moved) {
      context.commit('SWAP_IMPORTANT_ITEMS', moved);
    },
    SAVE_IMPORTANT_ITEMS (context) {
      const items = context.state.sidebarImportantItems;
      userService.saveImportantItems(items);
    },
    async TOGGLE_DASHBOARD_IMPORTANT (context, payload) {
      const dashboard = context.getters.getDashboardById(payload.dashboardId);
      const dashboardData = {
        id: dashboard.id,
        entity_type: 'DASHBOARD'
      };

      context.commit('TOGGLE_ITEM_IMPORTANT', {item: dashboardData, important: payload.important});
      return await context.dispatch('SAVE_IMPORTANT_ITEMS');
    },

    // onboarding special staff
    TOGGLE_ENABLED_ACCOUNTS_EXPANDING (context, active) {
      context.commit('TOGGLE_ENABLED_ACCOUNTS_EXPANDING', active);
    },
    COLLAPSE_ALL_ITEMS (context) {
      context.commit('COLLAPSE_ALL_ITEMS');
    }
  }
};

export default sidebarStore;
